import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { projectsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { 
  CheckCircle2, 
  FileText, 
  Loader2, 
  AlertCircle,
  Edit2,
  X,
  Check
} from 'lucide-react'
import { toast } from 'sonner'

interface ReferenceFile {
  id: string
  fileName: string
  parsedContent?: string
}

interface ReferenceInfoTableProps {
  projectId: string
  referenceFiles: ReferenceFile[]
  referenceConfirmed: boolean
  canConfirm: boolean
  onConfirmed?: () => void
}

export function ReferenceInfoTable({
  projectId,
  referenceFiles,
  referenceConfirmed,
  canConfirm,
  onConfirmed,
}: ReferenceInfoTableProps) {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')

  const confirmMutation = useMutation({
    mutationFn: () => projectsApi.confirmReference(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      toast.success('参考信息已确认，可以启动翻译')
      onConfirmed?.()
    },
    onError: (err: any) => {
      toast.error(`确认失败: ${err.error || err.message || '未知错误'}`)
    },
  })

  const handleEdit = (file: ReferenceFile) => {
    setEditingId(file.id)
    setEditContent(file.parsedContent || '')
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditContent('')
  }

  const handleSave = async (fileId: string) => {
    try {
      await projectsApi.updateReferenceFile(projectId, fileId, editContent)
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      toast.success('参考信息已更新')
      setEditingId(null)
    } catch (error) {
      toast.error('更新失败')
    }
  }

  if (!referenceFiles || referenceFiles.length === 0) {
    return null
  }

  return (
    <Card className="border-warning/30 bg-warning/10">
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-warning" />
            <CardTitle className="text-sm font-medium">参考信息一览表</CardTitle>
            {referenceConfirmed ? (
              <Badge variant="secondary" className="bg-success/10 text-success-foreground text-[10px]">
                <CheckCircle2 className="h-3 w-3 mr-1" /> 已确认
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-warning/15 text-warning-foreground text-[10px]">
                <AlertCircle className="h-3 w-3 mr-1" /> 待确认
              </Badge>
            )}
          </div>
          {!referenceConfirmed && canConfirm && (
            <Button
              size="sm"
              className="h-7 text-xs bg-warning hover:bg-warning/80"
              onClick={() => confirmMutation.mutate()}
              disabled={confirmMutation.isPending}
            >
              {confirmMutation.isPending ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3 w-3 mr-1" />
              )}
              确认参考信息
            </Button>
          )}
        </div>
        <CardDescription className="text-xs text-warning-foreground/80">
          请核对以下参考文件内容，确认后才能启动AI翻译。参考文件中与原文匹配的内容将用于辅助翻译。
        </CardDescription>
      </CardHeader>
      <CardContent className="py-2 px-4">
        <div className="space-y-3">
          {referenceFiles.map((file) => (
            <div key={file.id} className="border rounded-lg bg-white p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium">{file.fileName}</span>
                </div>
                {editingId === file.id ? (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => handleSave(file.id)}
                    >
                      <Check className="h-3.5 w-3.5 text-success" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={handleCancel}
                    >
                      <X className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                ) : (
                  !referenceConfirmed && canConfirm && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => handleEdit(file)}
                    >
                      <Edit2 className="h-3 w-3 text-muted-foreground" />
                    </Button>
                  )
                )}
              </div>
              {editingId === file.id ? (
                <Textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="min-h-[120px] text-xs"
                  placeholder="参考文件内容..."
                />
              ) : (
                <div className="text-xs text-muted-foreground whitespace-pre-wrap max-h-[200px] overflow-y-auto bg-muted/30 p-2 rounded">
                  {file.parsedContent || (
                    <span className="italic">内容解析中或无内容...</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
